import { useCallback, useState } from 'react';
import { Link, Navigate, createBrowserRouter } from 'react-router-dom';

import { Layout } from '../components/Layout';
import { DataTable, type TableColumn } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { Button, Card } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedResource } from '../hooks/usePaginatedResource';
import {
  billingApi,
  type AdminAuditLogFilters,
  type AdminAuditLogRecord,
  type ProviderRecord,
} from '../lib/api';
import { ApplicationManagePage, ApplicationsPage } from '../pages/ApplicationsPage';
import { CustomerTimelinePage } from '../pages/CustomerTimelinePage';
import { DetailPage } from '../pages/DetailPage';
import { OverviewPage } from '../pages/OverviewPage';
import { ProductManagePage, ProductsPage } from '../pages/ProductsPage';
import { ReviewCenterPage } from '../pages/ReviewCenterPage';
import { ResourcePage } from '../pages/ResourcePage';
import { useT, userError } from '../i18n/messages';

function dateCell<T>(field: keyof T) {
  return (row: T) => String((row[field] as unknown) ?? '—');
}

function listCell<T>(field: keyof T) {
  return (row: T) => {
    const value = row[field] as unknown;
    if (!Array.isArray(value) || value.length === 0) {
      return '—';
    }
    return value
      .map((entry) =>
        typeof entry === 'string'
          ? entry
          : typeof entry === 'object' && entry !== null
            ? Object.values(entry).join(' · ')
            : String(entry),
      )
      .join(', ');
  };
}

function boolCell<T>(field: keyof T, yes: string, no: string) {
  return (row: T) => ((row[field] as unknown) ? yes : no);
}

function textCell<T>(field: keyof T) {
  return (row: T) => {
    const value = row[field] as unknown;
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };
}

function ProviderDetail({ provider }: { provider: ProviderRecord }) {
  const t = useT();
  const capabilityColumns: TableColumn<ProviderRecord['capabilities'][number]>[] = [
    { key: 'key', label: 'billing.column.displayName' },
    {
      key: 'key',
      label: 'billing.column.providerType',
      render: (capability) => capability.key,
    },
    { key: 'status', label: 'billing.column.status', maxWidth: 120 },
    {
      key: 'notes',
      label: 'billing.column.details',
      render: (capability) => capability.notes ?? '—',
    },
  ];

  return (
    <div className="dialog-section-stack">
      <section className="panel dialog-section">
        <div className="panel-heading">
          <h3>{provider.display_name}</h3>
          <span className="detail-badge">{provider.status}</span>
        </div>
        <dl className="detail-list">
          <div>
            <dt>{t('billing.column.key')}</dt>
            <dd>{provider.key}</dd>
          </div>
          <div>
            <dt>{t('billing.ui.type')}</dt>
            <dd>{provider.provider_type}</dd>
          </div>
          <div>
            <dt>{t('billing.resource.updatedAt')}</dt>
            <dd>{provider.updated_at}</dd>
          </div>
          <div>
            <dt>{t('billing.resource.createdAt')}</dt>
            <dd>{provider.created_at}</dd>
          </div>
        </dl>
      </section>
      <section className="panel dialog-section">
        <div className="panel-heading">
          <h3>{t('billing.ui.capabilities')}</h3>
        </div>
        <DataTable
          columns={capabilityColumns}
          emptyMessage={t('billing.ui.no.provider.capabilities.were.returned')}
          rows={provider.capabilities}
          searchable
          searchPlaceholder={t('billing.ui.search.capabilities')}
        />
      </section>
      <section className="panel dialog-section">
        <div className="panel-heading">
          <h3>{t('billing.ui.configuration')}</h3>
        </div>
        <pre className="detail-json">{JSON.stringify(provider.config, null, 2)}</pre>
      </section>
    </div>
  );
}

export function useResourceColumns() {
  const t = useT();
  return {
    applicationColumns: [
      { key: 'key', label: 'billing.column.key' },
      { key: 'display_name', label: 'billing.column.displayName' },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      {
        key: 'domains',
        label: 'billing.column.domains',
        render: listCell('domains'),
      },
      {
        key: 'service_clients',
        label: 'billing.column.serviceClients',
        render: listCell('service_clients'),
      },
      {
        key: 'webhook_endpoints',
        label: 'billing.column.webhooks',
        render: listCell('webhook_endpoints'),
      },
    ] satisfies TableColumn[],
    providerColumns: [
      { key: 'key', label: 'billing.column.key' },
      { key: 'display_name', label: 'billing.column.displayName' },
      {
        key: 'provider_type',
        label: 'billing.column.providerType',
        maxWidth: 140,
      },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      {
        key: 'updated_at',
        label: 'billing.resource.updatedAt',
        render: dateCell('updated_at'),
      },
    ] satisfies TableColumn<ProviderRecord>[],
    productColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'key', label: 'billing.column.key' },
      { key: 'display_name', label: 'billing.column.displayName' },
      {
        key: 'product_type',
        label: 'billing.column.productType',
        maxWidth: 140,
      },
      { key: 'visibility', label: 'billing.column.status', maxWidth: 120 },
      {
        key: 'is_featured',
        label: 'billing.column.details',
        render: boolCell('is_featured', t('billing.status.yes'), t('billing.status.no')),
        maxWidth: 100,
      },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
    ] satisfies TableColumn[],
    customerColumns: [
      { key: 'id', label: 'billing.column.key' },
      { key: 'primary_email_snapshot', label: 'billing.column.email' },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      {
        key: 'applications',
        label: 'billing.column.links',
        render: listCell('applications'),
      },
      {
        key: 'provider_customers',
        label: 'billing.column.providerCustomers',
        render: listCell('provider_customers'),
      },
    ] satisfies TableColumn[],
    subscriptionColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'application_user_id', label: 'billing.column.applicationUser' },
      { key: 'billing_product_key', label: 'billing.column.product' },
      { key: 'provider_key', label: 'billing.column.provider' },
      {
        key: 'provider_subscription_id',
        label: 'billing.column.providerSubscriptionId',
      },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'source', label: 'billing.column.source', maxWidth: 120 },
      {
        key: 'current_period_end',
        label: 'billing.column.period',
        render: dateCell('current_period_end'),
      },
    ] satisfies TableColumn[],
    checkoutSessionColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'billing_product_key', label: 'billing.column.product' },
      { key: 'provider_key', label: 'billing.column.provider', maxWidth: 120 },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'provider_checkout_url', label: 'billing.column.checkoutUrl' },
      {
        key: 'expires_at',
        label: 'billing.column.expiresAt',
        render: dateCell('expires_at'),
      },
    ] satisfies TableColumn[],
    invoiceColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'invoice_number', label: 'billing.column.invoiceNumber' },
      { key: 'provider_key', label: 'billing.column.provider', maxWidth: 120 },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'currency', label: 'billing.column.currency', maxWidth: 100 },
      { key: 'amount_due', label: 'billing.column.amountDue', maxWidth: 120 },
      { key: 'amount_paid', label: 'billing.column.amountPaid', maxWidth: 120 },
      {
        key: 'paid_at',
        label: 'billing.column.paidAt',
        render: dateCell('paid_at'),
      },
    ] satisfies TableColumn[],
    transactionColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      {
        key: 'transaction_type',
        label: 'billing.column.transactionType',
        maxWidth: 140,
      },
      { key: 'provider_key', label: 'billing.column.provider', maxWidth: 120 },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'currency', label: 'billing.column.currency', maxWidth: 100 },
      { key: 'amount', label: 'billing.column.amount', maxWidth: 120 },
      {
        key: 'occurred_at',
        label: 'billing.column.occurredAt',
        render: dateCell('occurred_at'),
      },
    ] satisfies TableColumn[],
    eventColumns: [
      { key: 'record_kind', label: 'billing.column.kind', maxWidth: 140 },
      { key: 'provider_key', label: 'billing.column.provider', maxWidth: 120 },
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'event_type', label: 'billing.column.eventType' },
      {
        key: 'processing_status',
        label: 'billing.column.processing',
        maxWidth: 140,
      },
      {
        key: 'signature_verified',
        label: 'billing.column.signatureVerified',
        render: boolCell('signature_verified', t('billing.status.yes'), t('billing.status.no')),
      },
      {
        key: 'occurred_at',
        label: 'billing.resource.createdAt',
        render: dateCell('occurred_at'),
      },
    ] satisfies TableColumn[],
    deliveryColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'event_type', label: 'billing.column.eventType' },
      { key: 'webhook_url', label: 'billing.column.webhook' },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'attempt_count', label: 'billing.column.attempts', maxWidth: 100 },
      {
        key: 'next_attempt_at',
        label: 'billing.column.nextAttempt',
        render: dateCell('next_attempt_at'),
      },
      {
        key: 'error_message',
        label: 'billing.column.error',
        render: textCell('error_message'),
      },
    ] satisfies TableColumn[],
    reconciliationColumns: [
      {
        key: 'reconciliation_type',
        label: 'billing.column.kind',
        maxWidth: 140,
      },
      { key: 'entity_type', label: 'billing.column.key', maxWidth: 140 },
      { key: 'entity_id', label: 'billing.column.details' },
      { key: 'result_status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'reconciliation_action', label: 'billing.column.details' },
      {
        key: 'created_at',
        label: 'billing.resource.createdAt',
        render: dateCell('created_at'),
      },
    ] satisfies TableColumn[],
    replayColumns: [
      { key: 'replay_scope', label: 'billing.column.kind', maxWidth: 140 },
      { key: 'replay_status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'replayed_count', label: 'billing.column.amount', maxWidth: 100 },
      { key: 'failed_count', label: 'billing.column.error', maxWidth: 100 },
      {
        key: 'filter_criteria',
        label: 'billing.column.details',
        render: textCell('filter_criteria'),
      },
      {
        key: 'created_at',
        label: 'billing.resource.createdAt',
        render: dateCell('created_at'),
      },
    ] satisfies TableColumn[],
    auditColumns: [
      {
        key: 'created_at',
        label: 'billing.resource.createdAt',
        render: dateCell('created_at'),
      },
      {
        key: 'actor_id',
        label: 'billing.column.actor',
        render: textCell('actor_id'),
      },
      { key: 'action', label: 'billing.column.action' },
      { key: 'target_type', label: 'billing.column.kind', maxWidth: 150 },
      {
        key: 'target_display_name',
        label: 'billing.column.details',
        render: textCell('target_display_name'),
      },
      { key: 'result_status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'request_id', label: 'billing.column.requestId' },
    ] satisfies TableColumn<AdminAuditLogRecord>[],
    portalColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'application_user_id', label: 'billing.column.applicationUser' },
      { key: 'provider_key', label: 'billing.column.provider', maxWidth: 120 },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      { key: 'portal_url', label: 'billing.column.checkoutUrl' },
      {
        key: 'created_at',
        label: 'billing.resource.createdAt',
        render: dateCell('created_at'),
      },
    ] satisfies TableColumn[],
    previewColumns: [
      { key: 'application_key', label: 'billing.column.application' },
      { key: 'subscription_id', label: 'billing.column.key' },
      { key: 'current_product_key', label: 'billing.column.product' },
      { key: 'target_product_key', label: 'billing.column.details' },
      {
        key: 'provider_support_status',
        label: 'billing.column.status',
        maxWidth: 140,
      },
      {
        key: 'is_estimate',
        label: 'billing.column.details',
        render: boolCell('is_estimate', t('billing.status.yes'), t('billing.status.no')),
      },
      {
        key: 'created_at',
        label: 'billing.resource.createdAt',
        render: dateCell('created_at'),
      },
    ] satisfies TableColumn[],
  };
}

function ProvidersPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage<ProviderRecord>
      titleKey="billing.page.providers"
      testId="billing-providers-page"
      load={billingApi.getProviders}
      columns={columns.providerColumns}
      detail={{
        action: 'manage',
        actionLabel: t('billing.ui.manage.provider'),
        getTitle: (provider) => `Manage ${String(provider.display_name ?? 'provider')}`,
        render: (data) => <ProviderDetail provider={data as ProviderRecord} />,
        size: 'large',
      }}
    />
  );
}

function CustomersPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.customers"
      testId="billing-customers-page"
      load={billingApi.getCustomers}
      columns={columns.customerColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.customer'),
        getTitle: () => t('billing.page.customerDetail'),
        load: billingApi.getCustomer,
        render: (data, row) => (
          <div className="dialog-section-stack">
            <section className="panel dialog-section">
              <div className="panel-heading">
                <h3>{t('billing.ui.customer.timeline')}</h3>
                <Link className="inline-link" to={`/customers/${String(row.id)}`}>
                  {t('billing.ui.open.timeline')}
                </Link>
              </div>
              <pre className="detail-json">{JSON.stringify(data, null, 2)}</pre>
            </section>
          </div>
        ),
        size: 'large',
      }}
    />
  );
}

function SubscriptionsPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.subscriptions"
      testId="billing-subscriptions-page"
      load={billingApi.getSubscriptions}
      columns={columns.subscriptionColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.subscription'),
        getTitle: () => t('billing.page.subscriptionDetail'),
        load: billingApi.getSubscription,
        size: 'large',
      }}
    />
  );
}

function CheckoutSessionsPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.checkoutSessions"
      testId="billing-checkout-sessions-page"
      load={billingApi.getCheckoutSessions}
      columns={columns.checkoutSessionColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.checkout.session'),
        getTitle: () => t('billing.page.checkoutSessionDetail'),
        load: billingApi.getCheckoutSession,
        size: 'large',
      }}
    />
  );
}

function InvoicesPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.invoices"
      testId="billing-invoices-page"
      load={billingApi.getInvoices}
      columns={columns.invoiceColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.invoice'),
        getTitle: () => t('billing.page.invoiceDetail'),
        load: billingApi.getInvoice,
        size: 'large',
      }}
    />
  );
}

function TransactionsPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.transactions"
      testId="billing-transactions-page"
      load={billingApi.getTransactions}
      columns={columns.transactionColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.transaction'),
        getTitle: () => t('billing.page.transactionDetail'),
        load: billingApi.getTransaction,
        size: 'large',
      }}
    />
  );
}

function EventsPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.events"
      testId="billing-events-page"
      load={billingApi.getEvents}
      columns={columns.eventColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.event'),
        getTitle: () => t('billing.ui.event.details'),
        size: 'large',
      }}
    />
  );
}

function DeliveriesPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.deliveries"
      testId="billing-deliveries-page"
      load={billingApi.getDeliveries}
      columns={columns.deliveryColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.delivery'),
        getTitle: () => t('billing.page.deliveryDetail'),
        load: billingApi.getDelivery,
        size: 'large',
      }}
    />
  );
}

function ReconciliationPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.reconciliation"
      testId="billing-reconciliation-page"
      load={billingApi.getReconciliationEvents}
      columns={columns.reconciliationColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.reconciliation.event'),
        getTitle: () => t('billing.ui.reconciliation.event.details'),
        size: 'large',
      }}
    />
  );
}

function ReplayOperationsPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.replays"
      testId="billing-replay-operations-page"
      load={billingApi.getReplayOperations}
      columns={columns.replayColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.replay.operation'),
        getTitle: () => t('billing.page.replayDetail'),
        load: billingApi.getReplayOperation,
        size: 'large',
      }}
    />
  );
}

function AuditLogsPage() {
  const t = useT();
  const columns = useResourceColumns();
  const { tokens } = useAuth();
  const [page, setPage] = useState(1);
  const [draftFilters, setDraftFilters] = useState<AdminAuditLogFilters>({});
  const [filters, setFilters] = useState<AdminAuditLogFilters>({});
  const loadAuditLogs = useCallback(
    (token: string, currentPage: number, signal?: AbortSignal) =>
      billingApi.getAuditLogs(token, currentPage, signal, filters),
    [filters],
  );
  const resource = usePaginatedResource(tokens?.access_token ?? '', page, loadAuditLogs);

  function updateFilter(key: keyof AdminAuditLogFilters, value: string) {
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function applyFilters() {
    setPage(1);
    setFilters(draftFilters);
  }

  function clearFilters() {
    setPage(1);
    setDraftFilters({});
    setFilters({});
  }

  if (!tokens) {
    return (
      <section className="panel page-panel">
        {t('billing.ui.your.session.is.missing.or.expired')}
      </section>
    );
  }

  return (
    <section className="page" data-testid="billing-audit-logs-page">
      <PageHeader title={t('billing.nav.auditLogs')} />
      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{t('billing.ui.filters')}</h3>
        </div>
        <div className="filter-grid">
          <input
            aria-label={t('billing.column.actor')}
            placeholder={t('billing.column.actor')}
            value={draftFilters.actor_id ?? ''}
            onChange={(event) => updateFilter('actor_id', event.target.value)}
          />
          <input
            aria-label={t('billing.column.action')}
            placeholder={t('billing.column.action')}
            value={draftFilters.action ?? ''}
            onChange={(event) => updateFilter('action', event.target.value)}
          />
          <input
            aria-label={t('billing.ui.target.type')}
            placeholder={t('billing.ui.target.type')}
            value={draftFilters.target_type ?? ''}
            onChange={(event) => updateFilter('target_type', event.target.value)}
          />
          <select
            aria-label={t('billing.ui.result')}
            value={draftFilters.result_status ?? ''}
            onChange={(event) => updateFilter('result_status', event.target.value)}
          >
            <option value="">{t('billing.ui.any.result')}</option>
            <option value="success">{t('billing.ui.success')}</option>
            <option value="failure">{t('billing.ui.failure')}</option>
          </select>
          <input
            aria-label={t('billing.column.requestId')}
            placeholder={t('billing.column.requestId')}
            value={draftFilters.request_id ?? ''}
            onChange={(event) => updateFilter('request_id', event.target.value)}
          />
          <input
            aria-label={t('billing.ui.created.from')}
            placeholder={t('billing.ui.created.from.iso')}
            value={draftFilters.created_from ?? ''}
            onChange={(event) => updateFilter('created_from', event.target.value)}
          />
          <input
            aria-label={t('billing.ui.created.to')}
            placeholder={t('billing.ui.created.to.iso')}
            value={draftFilters.created_to ?? ''}
            onChange={(event) => updateFilter('created_to', event.target.value)}
          />
        </div>
        <div className="record-actions">
          <Button onClick={applyFilters} type="button">
            {t('billing.ui.apply.filters')}
          </Button>
          <Button onClick={clearFilters} type="button" variant="secondary">
            {t('billing.ui.clear')}
          </Button>
        </div>
      </Card>

      {resource.status === 'loading' ? (
        <section className="panel page-panel">{t('billing.ui.loading.data')}</section>
      ) : null}
      {resource.status === 'error' ? (
        <Card className="panel panel-error page-panel">
          <p>{userError(resource.error, t)}</p>
          <Button onClick={resource.reload} type="button">
            {t('billing.resource.retry')}
          </Button>
        </Card>
      ) : null}
      {resource.status === 'success' && resource.data ? (
        <>
          <DataTable columns={columns.auditColumns} rows={resource.data.items} />
          <footer className="pagination">
            <Button
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.previous')}
            </Button>
            <span className="pagination-summary">
              {t('billing.pagination.summary', {
                page: resource.data.pagination.page,
                total: Math.max(resource.data.pagination.total_pages, 1),
              })}
            </span>
            <Button
              disabled={
                resource.data.pagination.total_pages === 0 ||
                page >= resource.data.pagination.total_pages
              }
              onClick={() => setPage((current) => current + 1)}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.next')}
            </Button>
          </footer>
        </>
      ) : null}
    </section>
  );
}

function PortalSessionsPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.portalSessions"
      testId="billing-portal-sessions-page"
      load={billingApi.getPortalSessions}
      columns={columns.portalColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.portal.session'),
        getTitle: () => t('billing.ui.portal.session.details'),
        size: 'large',
      }}
    />
  );
}

function SubscriptionPreviewsPage() {
  const t = useT();
  const columns = useResourceColumns();
  return (
    <ResourcePage
      titleKey="billing.page.subscriptionPreviews"
      testId="billing-subscription-previews-page"
      load={billingApi.getSubscriptionChangePreviews}
      columns={columns.previewColumns}
      detail={{
        action: 'view',
        actionLabel: t('billing.ui.view.subscription.preview'),
        getTitle: () => t('billing.ui.subscription.preview.details'),
        size: 'large',
      }}
    />
  );
}

function RouterError() {
  const t = useT();
  return (
    <section className="panel panel-error" role="alert">
      <p>{t('billing.error.unexpected')}</p>
      <Button onClick={() => window.location.reload()}>{t('billing.resource.retry')}</Button>
    </section>
  );
}

function RouterRoot() {
  return <Layout />;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RouterRoot />,
    errorElement: <RouterError />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'auth/callback', element: <Navigate to="/" replace /> },
      { path: 'review-center', element: <ReviewCenterPage /> },
      { path: 'applications', element: <ApplicationsPage /> },
      {
        path: 'applications/:applicationId',
        element: <ApplicationManagePage />,
      },
      { path: 'providers', element: <ProvidersPage /> },
      { path: 'products', element: <ProductsPage /> },
      { path: 'products/:productId', element: <ProductManagePage /> },
      {
        path: 'products/:productId/prices/:priceId',
        element: <ProductManagePage />,
      },
      { path: 'prices', element: <Navigate replace to="/products" /> },
      { path: 'customers', element: <CustomersPage /> },
      { path: 'customers/:customerId', element: <CustomerTimelinePage /> },
      { path: 'subscriptions', element: <SubscriptionsPage /> },
      {
        path: 'subscriptions/:subscriptionId',
        element: (
          <DetailPage
            titleKey="billing.page.subscriptionDetail"
            testId="billing-subscription-detail-page"
            paramKey="subscriptionId"
            load={billingApi.getSubscription}
          />
        ),
      },
      { path: 'checkout-sessions', element: <CheckoutSessionsPage /> },
      {
        path: 'checkout-sessions/:checkoutSessionId',
        element: (
          <DetailPage
            titleKey="billing.page.checkoutSessionDetail"
            testId="billing-checkout-session-detail-page"
            paramKey="checkoutSessionId"
            load={billingApi.getCheckoutSession}
          />
        ),
      },
      { path: 'invoices', element: <InvoicesPage /> },
      {
        path: 'invoices/:invoiceId',
        element: (
          <DetailPage
            titleKey="billing.page.invoiceDetail"
            testId="billing-invoice-detail-page"
            paramKey="invoiceId"
            load={billingApi.getInvoice}
          />
        ),
      },
      { path: 'transactions', element: <TransactionsPage /> },
      {
        path: 'transactions/:transactionId',
        element: (
          <DetailPage
            titleKey="billing.page.transactionDetail"
            testId="billing-transaction-detail-page"
            paramKey="transactionId"
            load={billingApi.getTransaction}
          />
        ),
      },
      { path: 'events', element: <EventsPage /> },
      { path: 'deliveries', element: <DeliveriesPage /> },
      {
        path: 'deliveries/:deliveryId',
        element: (
          <DetailPage
            titleKey="billing.page.deliveryDetail"
            testId="billing-delivery-detail-page"
            paramKey="deliveryId"
            load={billingApi.getDelivery}
          />
        ),
      },
      { path: 'reconciliation', element: <ReconciliationPage /> },
      { path: 'replays', element: <ReplayOperationsPage /> },
      {
        path: 'replays/:operationId',
        element: (
          <DetailPage
            titleKey="billing.page.replayDetail"
            testId="billing-replay-detail-page"
            paramKey="operationId"
            load={billingApi.getReplayOperation}
          />
        ),
      },
      { path: 'audit-logs', element: <AuditLogsPage /> },
      { path: 'portal-sessions', element: <PortalSessionsPage /> },
      { path: 'subscription-previews', element: <SubscriptionPreviewsPage /> },
    ],
  },
]);
